import { formatClosureDate } from "@/lib/account-closure";

export const ACCOUNT_DATA_EXPORT_PATH = "/app/settings/data-export";
export const ACCOUNT_DATA_EXPORT_FUNCTION = "account-data-export";
export const EXPORT_POLL_MS = 5000;

export type ExportJobStatus = "queued" | "running" | "ready" | "failed" | "expired";

export interface ExportPartLink {
  part: number;
  bytes: number;
  url: string | null;
  file_name: string;
}

export interface ExportJob {
  id: string;
  status: ExportJobStatus;
  requested_at: string;
  finished_at: string | null;
  expires_at: string | null;
  rows_exported: number;
  parts: ExportPartLink[];
}

export interface ExportStatus {
  job: ExportJob | null;
  can_request: boolean;
  blocked_reason: "in_progress" | "daily_limit" | null;
  retry_at: string | null;
  requests_left: number;
}

export function isExportActive(job: ExportJob | null | undefined): boolean {
  return job?.status === "queued" || job?.status === "running";
}

export function isExportDownloadable(job: ExportJob | null | undefined, now: Date = new Date()): boolean {
  if (job?.status !== "ready" || !job.expires_at) return false;
  return Date.parse(job.expires_at) > now.getTime() && job.parts.some((part) => !!part.url);
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

export function partLabel(part: ExportPartLink, total: number): string {
  return total > 1 ? `Descargar parte ${part.part} de ${total}` : "Descargar mis datos";
}

const ERROR_COPY: Record<string, string> = {
  in_progress: "Ya estamos preparando una descarga de esta cuenta. Te avisamos por correo cuando esté lista.",
  daily_limit: "Ya pediste 3 descargas en las últimas 24 horas. Intenta más tarde.",
  not_admin: "Solo un administrador de la cuenta puede descargar los datos.",
  not_flow: "La descarga de datos no está disponible para esta cuenta.",
  retention_over: "El plazo para descargar los datos de esta cuenta ya terminó.",
  not_found: "No encontramos la cuenta.",
  unauthorized: "Tu sesión venció. Vuelve a iniciar sesión.",
};

export function exportErrorMessage(code: string | null | undefined): string {
  return (code && ERROR_COPY[code]) || "No pudimos preparar la descarga. Intenta de nuevo en unos minutos.";
}

export function exportStatusText(status: ExportStatus | null, now: Date = new Date()): string | null {
  const job = status?.job;
  if (!job) return null;
  if (isExportActive(job)) {
    return job.rows_exported > 0
      ? `Estamos preparando tus datos (${job.rows_exported.toLocaleString("es-CO")} registros hasta ahora). Te enviamos un correo cuando esté lista; puedes cerrar esta página.`
      : "Estamos preparando tus datos. Te enviamos un correo cuando esté lista; puedes cerrar esta página.";
  }
  if (isExportDownloadable(job, now) && job.expires_at) {
    return `Tu descarga está lista. Los enlaces funcionan hasta el ${formatClosureDate(new Date(job.expires_at))}.`;
  }
  if (job.status === "failed") return "No pudimos terminar la última descarga. Pide una nueva.";
  return null;
}
